import { create } from 'zustand'
import { DEFAULT_CADENCE } from '../utils/config'
import {
   ICampaignConfiguration,
   ICriteria,
   IContentReference,
   IContentRevision,
   ICatalogItem,
   IConfigurationRead,
   TCampaignStatus,
   TContentKind,
   TContentDefinition,
} from '../interfaces/campaign-v2.types'
import type { TSlot } from '../components/content/useContentActions'

export type TContentDraft =
   | { mode: 'pick'; slot: TSlot }
   | { mode: 'write'; slot: TSlot; def: TContentDefinition }
   | {
        mode: 'edit'
        slot: TSlot
        ref: IContentReference
        base: IContentRevision
        shared: boolean
        def?: TContentDefinition
     }

export const contentDraftKey = (poolId: string, slot?: TSlot) =>
   JSON.stringify([poolId, slot || 'all'])
export const hasContentEdits = (drafts: Record<string, TContentDraft>) =>
   Object.values(drafts).some((d) => d.mode !== 'pick')

export type TEditorStep =
   | 'audience'
   | 'content'
   | 'cadence'
   | 'voice'
   | 'numbers'
   | 'team'
   | 'settings'
   | 'review'

export const EDITOR_STEPS: TEditorStep[] = [
   'audience',
   'content',
   'cadence',
   'voice',
   'numbers',
   'team',
   'settings',
   'review',
]

/** What the rail and the lists show for a bound content reference. */
export interface IContentSummary {
   id: string
   kind: TContentKind
   name: string
   shared: boolean
   businessTypeId?: number
   counts?: ICatalogItem['counts']
   callDirections?: ICatalogItem['callDirections']
   leadTypes?: ICatalogItem['leadTypes']
}

interface IEditorState {
   campaignId: string | null
   name: string
   config: ICampaignConfiguration | null
   revision: number
   status: TCampaignStatus
   publishedVersionId: string | null
   activeVersionId: string | null
   hasUnpublishedChanges: boolean
   /** Local edits not yet saved to the draft. */
   dirty: boolean
   step: TEditorStep
   /** Steps the user has completed at least once (rail states). */
   visited: TEditorStep[]
   categoryLabels: Record<number, string>
   contentIndex: Record<string, IContentSummary>
   /** "source:Zillow" → "Zillow", for readable audience sentences. */
   audienceLabels: Record<string, string>
   /** The campaign was changed elsewhere; the fresh read is kept here. */
   conflict: IConfigurationRead | null
   /** Local authoring state; never sent in the campaign payload. */
   contentDrafts: Record<string, TContentDraft>
   dynamicCriteria: ICriteria[]
   setContentDraft: (key: string, draft: TContentDraft | null) => void
   setSourceMode: (mode: 'manual' | 'dynamic') => void

   startNew: (config: ICampaignConfiguration, name: string) => void
   applyRead: (read: IConfigurationRead) => void
   setName: (name: string) => void
   setConfig: (
      updater: (config: ICampaignConfiguration) => ICampaignConfiguration
   ) => void
   setStep: (step: TEditorStep) => void
   markVisited: (step: TEditorStep) => void
   setCategoryLabels: (labels: Record<number, string>) => void
   indexContent: (items: IContentSummary[]) => void
   addAudienceLabels: (labels: Record<string, string>) => void
   setConflict: (read: IConfigurationRead | null) => void
   setStatus: (status: TCampaignStatus) => void
   reset: () => void
}

const initial = {
   campaignId: null,
   name: '',
   config: null,
   revision: 0,
   status: 'draft' as TCampaignStatus,
   publishedVersionId: null,
   activeVersionId: null,
   hasUnpublishedChanges: false,
   dirty: false,
   step: 'audience' as TEditorStep,
   visited: [] as TEditorStep[],
   categoryLabels: {},
   contentIndex: {},
   audienceLabels: {},
   conflict: null,
   contentDrafts: {} as Record<string, TContentDraft>,
   dynamicCriteria: [] as ICriteria[],
}

/**
 * The campaign being edited. One campaign at a time; the page resets it
 * when the route changes. Never persisted: the draft on the server is the
 * record, this is the working copy.
 */
export const useCampaignV2Store = create<IEditorState>((set) => ({
   ...initial,

   startNew: (config, name) =>
      set({
         ...initial,
         config,
         name,
         dirty: true,
         step: 'audience',
         visited: [],
      }),

   applyRead: (read) => {
      /* A draft never presents an empty timeline: the general reactivate
         cadence is prefilled (and left dirty so the next save keeps it). */
      const prefillCadence =
         read.status === 'draft' && !read.configuration.cadence?.length
      return set((s) => ({
         campaignId: read.campaignId,
         name: read.name,
         config: prefillCadence
            ? {
                 ...read.configuration,
                 cadence: DEFAULT_CADENCE.map((c) => ({ ...c })),
              }
            : read.configuration,
         revision: read.revision,
         status: read.status,
         publishedVersionId: read.publishedVersionId,
         activeVersionId: read.activeVersionId,
         hasUnpublishedChanges: read.hasUnpublishedChanges,
         dirty: prefillCadence,
         conflict: null,
         contentDrafts: s.campaignId === read.campaignId ? s.contentDrafts : {},
         dynamicCriteria:
            read.configuration.source.mode === 'dynamic'
               ? read.configuration.source.criteria
               : s.campaignId === read.campaignId
                 ? s.dynamicCriteria
                 : [],
         // Keep the user's place and labels when the same campaign is reread.
         step: s.campaignId === read.campaignId ? s.step : s.step,
      }))
   },

   setName: (name) => set({ name, dirty: true }),

   setContentDraft: (key, draft) =>
      set((s) => {
         const contentDrafts = { ...s.contentDrafts }
         if (draft) contentDrafts[key] = draft
         else delete contentDrafts[key]
         return { contentDrafts }
      }),

   setSourceMode: (mode) =>
      set((s) => {
         if (!s.config || s.config.source.mode === mode) return {}
         const criteria =
            s.config.source.mode === 'dynamic'
               ? s.config.source.criteria
               : s.dynamicCriteria
         return {
            dynamicCriteria: criteria,
            dirty: true,
            config: {
               ...s.config,
               source: mode === 'manual' ? { mode } : { mode, criteria },
            },
         }
      }),

   setConfig: (updater) =>
      set((s) => {
         if (!s.config) return {}
         const config = updater(s.config)
         const pools = new Set(config.contentPools.map((p) => p.id))
         const contentDrafts = Object.fromEntries(
            Object.entries(s.contentDrafts).filter(([key]) =>
               pools.has(JSON.parse(key)[0])
            )
         )
         return { config, contentDrafts, dirty: true }
      }),

   setStep: (step) => set({ step }),

   markVisited: (step) =>
      set((s) =>
         s.visited.includes(step) ? {} : { visited: [...s.visited, step] }
      ),

   setCategoryLabels: (categoryLabels) => set({ categoryLabels }),

   indexContent: (items) =>
      set((s) => {
         const next = { ...s.contentIndex }
         items.forEach((i) => {
            next[`${i.kind}:${i.id}`] = i
         })
         return { contentIndex: next }
      }),

   addAudienceLabels: (labels) =>
      set((s) => ({ audienceLabels: { ...s.audienceLabels, ...labels } })),

   setConflict: (conflict) => set({ conflict }),

   setStatus: (status) => set({ status }),

   reset: () => set({ ...initial }),
}))
